/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo } from 'react';

import { type InterpreterFrom } from 'xstate';

import { useResponsive } from '@sg/garnish';

import { type createLocationSearchMachine } from '../../machine';
import { type LocationResultsLists, LocationResultsListsV2 } from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders location lists using the V2 layout with separate recent, nearby,
 * and standard location groups.
 */
export const LocationResults = memo((props: LocationResultsProps) => {
  const {
    locationSearchService,
    locations = [],
    locationSearchType,
    activePinLocationId,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    isLoading = false,
    isUsingSearchField,
    withSafeAreaInsets = false,
    onCardPress,
    onCardFocus,
  } = props;

  const { minWidth } = useResponsive();

  const hasNoResults = !isLoading && locations.length === 0;

  if (hasNoResults) return null;

  // ─── SM ──────────────────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <LocationResultsListsV2.SM
        variation="standard"
        locationSearchService={locationSearchService}
        selectedLocationSearchType={locationSearchType}
        onLocationCardFocus={onCardFocus}
        focusedLocationId={activePinLocationId}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
        isLoading={isLoading}
        isUsingSearchField={isUsingSearchField}
        locations={locations}
        onLocationCardPress={onCardPress}
      />
    );
  }

  // ─── XS ──────────────────────────────────────────────────────────────

  return (
    <LocationResultsListsV2.XS
      variation="standard"
      locationSearchService={locationSearchService}
      selectedLocationSearchType={locationSearchType}
      onLocationCardFocus={onCardFocus}
      focusedLocationId={activePinLocationId}
      interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      isLoading={isLoading}
      withSafeAreaInsets={withSafeAreaInsets}
      isUsingSearchField={isUsingSearchField}
      locations={locations}
      onLocationCardPress={onCardPress}
    />
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsProps = Readonly<{
  locationSearchService: InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>; // prettier-ignore
  activePinLocationId: string | undefined;
  shouldHideDetailsModal: boolean;
  onLocationResultsExpandCollapse?: (isExpanded: boolean) => void;
  withSafeAreaInsets?: boolean;
  interactedLocationDeliveryAddressId?: string;
  isUsingSearchField: boolean;
}> &
  Pick<
    ComponentProps<typeof LocationResultsLists>,
    | 'locations'
    | 'otherLocations'
    | 'locationSearchType'
    | 'interactedLocationRestaurantSlug'
    | 'isLoading'
    | 'onCardPress'
    | 'onCardFocus'
  >;
